import { FC, useCallback, useEffect, useState } from 'react'
import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
} from '@mui/material'
import { SelectChangeEvent } from '@mui/material/Select'
import AddIcon from '@mui/icons-material/Add'
import RemoveIcon from '@mui/icons-material/Remove'

import { AppDialog } from '../../../components'
import CustomNumberField from '../Users/profile/CustomNumberField'
import {
  EditItemCycleRatePlanRequest,
  EntryCycleRatePlanApi,
  SiteRateCategoryGroup,
  SiteRateItemRow,
  UpdateSiteItemRateRequest,
  updateSiteItemRateFull,
} from '../../../services'
import { useAppDispatch } from '../../../hooks'
import { showAlertAction } from '../../../store/alerts'
import {
  type RentCycleMode,
  type ExtraDayChargeMode,
  formatPerDayFromCycle,
  hydrateExtraDayFromPlan,
} from './editCycleRatePlanUtils'

export type EditSiteItemRateTarget =
  | { kind: 'item'; row: SiteRateItemRow }
  | { kind: 'category'; category: SiteRateCategoryGroup }

export interface EditSiteItemRateProps {
  siteId: number
  target: EditSiteItemRateTarget
  onClose: () => void
  onSuccess: () => void
}

function buildUpdatePayload(
  siteId: number,
  target: EditSiteItemRateTarget,
  rest: Omit<UpdateSiteItemRateRequest, 'siteId' | 'itemId' | 'categoryId'>
): UpdateSiteItemRateRequest {
  if (target.kind === 'item') {
    return { siteId, itemId: target.row.itemId, ...rest }
  }
  return { siteId, categoryId: target.category.categoryId, ...rest }
}

export const EditSiteItemRate: FC<EditSiteItemRateProps> = ({
  siteId,
  target,
  onClose,
  onSuccess,
}) => {
  const dispatchAction = useAppDispatch()
  const [closed, setClosed] = useState(false)
  const [submitting, setSubmitting] = useState(false)

  const [rentCycleMode, setRentCycleMode] = useState<RentCycleMode>('DAILY')
  const [customCycleDays, setCustomCycleDays] = useState<number>(30)
  const [cyclePlanId, setCyclePlanId] = useState<number | undefined>(undefined)
  const [slabRows, setSlabRows] = useState<{ uptoDays: string; charge: string }[]>([
    { uptoDays: '', charge: '' },
  ])
  const [slabError, setSlabError] = useState(false)
  const [extraDayChargeMode, setExtraDayChargeMode] =
    useState<ExtraDayChargeMode>('PER_DAY_RATE')
  const [perDaySideField, setPerDaySideField] = useState('')

  const [rateField, setRateField] = useState<{
    value: string | number
    error: boolean
  }>({ value: '', error: false })

  const hydrateFromRateAndPlan = useCallback(
    (rate: number | null, plan: EntryCycleRatePlanApi | null | undefined) => {
      if (plan) {
        setRentCycleMode(plan.cycleDays === 30 ? 'MONTHLY' : 'CUSTOM')
        setCustomCycleDays(plan.cycleDays)
        setCyclePlanId(plan.id)
        const hydrated = hydrateExtraDayFromPlan(plan)
        setExtraDayChargeMode(hydrated.mode)
        setSlabRows(hydrated.slabRows)
        setPerDaySideField(hydrated.perDaySideField)
        setRateField({ value: plan.cycleCharge, error: false })
      } else {
        setRentCycleMode('DAILY')
        setCustomCycleDays(30)
        setCyclePlanId(undefined)
        setExtraDayChargeMode('PER_DAY_RATE')
        setSlabRows([{ uptoDays: '', charge: '' }])
        setPerDaySideField('')
        setRateField({ value: rate ?? '', error: false })
      }
      setSlabError(false)
    },
    []
  )

  const hydrateFromTarget = useCallback(
    (t: EditSiteItemRateTarget) => {
      if (t.kind === 'item') {
        hydrateFromRateAndPlan(t.row.rate, t.row.cycleRatePlan)
      } else {
        hydrateFromRateAndPlan(t.category.rate, t.category.cycleRatePlan)
      }
    },
    [hydrateFromRateAndPlan]
  )

  useEffect(() => {
    hydrateFromTarget(target)
  }, [target, hydrateFromTarget])

  const dialogTitle =
    target.kind === 'item' ? target.row.itemName : target.category.name

  const baseDailyRate =
    target.kind === 'item' ? target.row.rate : target.category.rate

  const onChangeRate = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { valueAsNumber, value } = e.target
    const parsed =
      value === '' || value == null
        ? NaN
        : typeof valueAsNumber === 'number' && !Number.isNaN(valueAsNumber)
          ? valueAsNumber
          : Number(value)
    if (!Number.isNaN(parsed)) {
      setRateField({ value: parsed, error: false })
    } else {
      setRateField({ value: value, error: true })
    }
  }

  const onRentCycleModeChange = (event: SelectChangeEvent<RentCycleMode>) => {
    const mode = event.target.value as RentCycleMode
    const wasDaily = rentCycleMode === 'DAILY'
    setRentCycleMode(mode)
    setSlabError(false)
    if (mode === 'DAILY') {
      setExtraDayChargeMode('PER_DAY_RATE')
      setPerDaySideField('')
    } else if (wasDaily) {
      setExtraDayChargeMode('PER_DAY_RATE')
      setSlabRows([{ uptoDays: '', charge: '' }])
      const rateVal = Number(rateField.value)
      const days = mode === 'MONTHLY' ? 30 : customCycleDays
      if (rateVal > 0 && days > 0 && !Number.isNaN(rateVal)) {
        setPerDaySideField(formatPerDayFromCycle(rateVal, days))
      } else {
        setPerDaySideField('')
      }
    }
  }

  const onExtraDayChargeModeChange = (
    event: SelectChangeEvent<ExtraDayChargeMode>
  ) => {
    const v = event.target.value as ExtraDayChargeMode
    const prev = extraDayChargeMode
    setExtraDayChargeMode(v)
    setSlabError(false)
    const rateVal = Number(rateField.value)
    const days = rentCycleMode === 'MONTHLY' ? 30 : customCycleDays

    if (v === 'PER_DAY_RATE') {
      if (rateVal > 0 && days > 0 && !Number.isNaN(rateVal)) {
        setPerDaySideField(formatPerDayFromCycle(rateVal, days))
      } else {
        setPerDaySideField('')
      }
    } else if (v === 'PER_DAY_FIXED' && prev !== 'PER_DAY_FIXED') {
      setPerDaySideField('')
    } else if (v === 'BY_DAY_RANGE') {
      setSlabRows((rows) => (rows.length ? rows : [{ uptoDays: '', charge: '' }]))
    }
  }

  useEffect(() => {
    if (extraDayChargeMode !== 'PER_DAY_RATE') return
    if (rentCycleMode === 'DAILY') return
    const rateVal = Number(rateField.value)
    const days = rentCycleMode === 'MONTHLY' ? 30 : customCycleDays
    if (
      !rateVal ||
      rateVal <= 0 ||
      !days ||
      days <= 0 ||
      Number.isNaN(rateVal)
    ) {
      return
    }
    setPerDaySideField(formatPerDayFromCycle(rateVal, days))
  }, [extraDayChargeMode, rentCycleMode, customCycleDays, rateField.value])

  const onPerDaySideFieldChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPerDaySideField(e.target.value)
  }

  const onCustomCycleDaysChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value
    if (raw === '' || raw == null) {
      setCustomCycleDays(0)
      return
    }
    const n = parseInt(String(raw), 10)
    if (!Number.isNaN(n)) setCustomCycleDays(n)
  }

  const updateSlab = (index: number, field: 'uptoDays' | 'charge', v: string) => {
    setSlabRows((rows) =>
      rows.map((r, i) => (i === index ? { ...r, [field]: v } : r))
    )
    setSlabError(false)
  }

  const addSlabRow = () => {
    setSlabRows((rows) =>
      rows.length >= 3 ? rows : [...rows, { uptoDays: '', charge: '' }]
    )
  }

  const removeSlabRow = (index: number) => {
    setSlabRows((rows) => (rows.length <= 1 ? rows : rows.filter((_, i) => i !== index)))
    setSlabError(false)
  }

  const buildSlabsForApi = () =>
    slabRows
      .map((r) => ({
        uptoDays: Number(r.uptoDays),
        charge: Number(r.charge),
      }))
      .filter(
        (s) =>
          !Number.isNaN(s.uptoDays) &&
          s.uptoDays > 0 &&
          !Number.isNaN(s.charge) &&
          s.charge >= 0
      )

  const handleDialogClose = () => {
    setClosed(true)
    onClose()
  }

  const submitHandle = async () => {
    if (submitting) return
    const rateVal = Number(rateField.value)

    if (rentCycleMode !== 'DAILY') {
      if (!rateVal || rateVal <= 0) {
        setRateField((s) => ({ ...s, error: true }))
        dispatchAction(
          showAlertAction({
            message: 'Please enter a valid cycle charge (rate)',
            severity: 'error',
          })
        )
        return
      }
      if (rentCycleMode === 'CUSTOM') {
        if (
          !customCycleDays ||
          customCycleDays < 1 ||
          customCycleDays > 365 ||
          !Number.isInteger(customCycleDays)
        ) {
          dispatchAction(
            showAlertAction({
              message: 'Cycle days must be an integer between 1 and 365',
              severity: 'error',
            })
          )
          return
        }
      }
      const effectiveCycleDays = rentCycleMode === 'MONTHLY' ? 30 : customCycleDays

      const cyclePlanBase: Pick<
        EditItemCycleRatePlanRequest,
        'id' | 'cycleDays' | 'cycleCharge'
      > = {
        id: cyclePlanId,
        cycleDays: rentCycleMode === 'MONTHLY' ? 30 : customCycleDays,
        cycleCharge: rateVal,
      }

      if (extraDayChargeMode === 'BY_DAY_RANGE') {
        for (const row of slabRows) {
          const hasU = row.uptoDays !== ''
          const hasC = row.charge !== ''
          if (hasU !== hasC) {
            setSlabError(true)
            dispatchAction(
              showAlertAction({
                message:
                  'Complete both Extra days and Charge for each slab row',
                severity: 'error',
              })
            )
            return
          }
          if (hasU) {
            const d = Number(row.uptoDays)
            const c = Number(row.charge)
            if (Number.isNaN(d) || d <= 0 || Number.isNaN(c) || c < 0) {
              setSlabError(true)
              dispatchAction(
                showAlertAction({
                  message: 'Invalid slab row values',
                  severity: 'error',
                })
              )
              return
            }
          }
        }

        const uptoForDup: number[] = []
        for (const row of slabRows) {
          if (row.uptoDays !== '') {
            const d = Number(row.uptoDays)
            if (!Number.isNaN(d) && d > 0) uptoForDup.push(d)
          }
        }
        if (uptoForDup.length !== new Set(uptoForDup).size) {
          setSlabError(true)
          dispatchAction(
            showAlertAction({
              message: 'Each Extra days value must be unique',
              severity: 'error',
            })
          )
          return
        }

        for (const row of slabRows) {
          if (row.uptoDays === '') continue
          const d = Number(row.uptoDays)
          if (Number.isNaN(d) || d <= 0) continue
          if (d > effectiveCycleDays) {
            setSlabError(true)
            dispatchAction(
              showAlertAction({
                message: `Extra days must be less than or equal to cycle days (${effectiveCycleDays})`,
                severity: 'error',
              })
            )
            return
          }
        }

        const cycleRatePlan: EditItemCycleRatePlanRequest = {
          ...cyclePlanBase,
          extraDayChargeSlabs: buildSlabsForApi(),
        }
        setSubmitting(true)
        try {
          const res = await updateSiteItemRateFull(
            buildUpdatePayload(siteId, target, { cycleRatePlan })
          )
          if (!res.success) {
            throw new Error(res.message || 'Update failed')
          }
          dispatchAction(
            showAlertAction({
              message: 'Site rate updated successfully',
              severity: 'success',
            })
          )
          onSuccess()
          handleDialogClose()
        } catch (err: unknown) {
          const msg =
            err instanceof Error ? err.message : 'Failed to update site rate'
          dispatchAction(showAlertAction({ message: msg, severity: 'error' }))
        } finally {
          setSubmitting(false)
        }
        return
      }

      if (extraDayChargeMode === 'PER_DAY_FIXED') {
        const p = Number(perDaySideField)
        if (Number.isNaN(p) || p <= 0 || !Number.isFinite(p)) {
          dispatchAction(
            showAlertAction({
              message: 'Enter a valid per day fixed amount',
              severity: 'error',
            })
          )
          return
        }
        const cycleRatePlan: EditItemCycleRatePlanRequest = {
          ...cyclePlanBase,
          perDayFixed: p,
        }
        setSubmitting(true)
        try {
          const res = await updateSiteItemRateFull(
            buildUpdatePayload(siteId, target, { cycleRatePlan })
          )
          if (!res.success) throw new Error(res.message || 'Update failed')
          dispatchAction(
            showAlertAction({
              message: 'Site rate updated successfully',
              severity: 'success',
            })
          )
          onSuccess()
          handleDialogClose()
        } catch (err: unknown) {
          const msg =
            err instanceof Error ? err.message : 'Failed to update site rate'
          dispatchAction(showAlertAction({ message: msg, severity: 'error' }))
        } finally {
          setSubmitting(false)
        }
        return
      }

      const p = Number(perDaySideField)
      if (Number.isNaN(p) || p <= 0 || !Number.isFinite(p)) {
        dispatchAction(
          showAlertAction({
            message: 'Enter a valid per day rate',
            severity: 'error',
          })
        )
        return
      }
      const cycleRatePlan: EditItemCycleRatePlanRequest = {
        ...cyclePlanBase,
        perDayRate: p,
      }
      setSubmitting(true)
      try {
        const res = await updateSiteItemRateFull(
          buildUpdatePayload(siteId, target, { cycleRatePlan })
        )
        if (!res.success) throw new Error(res.message || 'Update failed')
        dispatchAction(
          showAlertAction({
            message: 'Site rate updated successfully',
            severity: 'success',
          })
        )
        onSuccess()
        handleDialogClose()
      } catch (err: unknown) {
        const msg =
          err instanceof Error ? err.message : 'Failed to update site rate'
        dispatchAction(showAlertAction({ message: msg, severity: 'error' }))
      } finally {
        setSubmitting(false)
      }
      return
    }

    const dailyRate = Number.isNaN(rateVal) ? (baseDailyRate ?? 0) : rateVal
    if (Number.isNaN(dailyRate) || dailyRate < 0) {
      setRateField((s) => ({ ...s, error: true }))
      dispatchAction(
        showAlertAction({
          message: 'Please enter a valid rate',
          severity: 'error',
        })
      )
      return
    }

    setSubmitting(true)
    try {
      const res = await updateSiteItemRateFull(
        buildUpdatePayload(siteId, target, { rate: dailyRate })
      )
      if (!res.success) throw new Error(res.message || 'Update failed')
      dispatchAction(
        showAlertAction({
          message: 'Site rate updated successfully',
          severity: 'success',
        })
      )
      onSuccess()
      handleDialogClose()
    } catch (err: unknown) {
      const msg =
        err instanceof Error ? err.message : 'Failed to update site rate'
      dispatchAction(showAlertAction({ message: msg, severity: 'error' }))
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <AppDialog
      id="edit-site-item-rate"
      title={dialogTitle}
      submitButtonText="Submit"
      onClose={handleDialogClose}
      onSubmit={() => void submitHandle()}
      close={closed}
      maxWidth="sm"
    >
      <Box sx={{ position: 'relative', opacity: submitting ? 0.55 : 1, pointerEvents: submitting ? 'none' : 'auto' }}>
        {submitting ? (
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              zIndex: 2,
            }}
          >
            <CircularProgress size={32} />
          </Box>
        ) : null}
        <Grid
          container
          direction="row"
          justifyContent="center"
          alignItems="stretch"
          spacing={3}
        >
          <Grid item xs={12} sx={{ pb: 2, mx: 2 }} mt={1.5}>
            <Box component="form" noValidate autoComplete="off">
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <Grid container spacing={2} alignItems="flex-start">
                    <Grid
                      item
                      xs={12}
                      sm={rentCycleMode === 'DAILY' ? 6 : 4}
                    >
                      <CustomNumberField
                        maxLength={8}
                        name="rate"
                        value={rateField.value}
                        label="Rate"
                        onChange={onChangeRate}
                        fullWidth
                        error={rateField.error}
                        helperText={rateField.error ? 'Please enter rate' : ''}
                      />
                    </Grid>
                    <Grid
                      item
                      xs={12}
                      sm={rentCycleMode === 'DAILY' ? 6 : 4}
                    >
                      <FormControl fullWidth size="small">
                        <InputLabel id="site-edit-rent-cycle-label">Rent cycle</InputLabel>
                        <Select
                          labelId="site-edit-rent-cycle-label"
                          label="Rent cycle"
                          value={rentCycleMode}
                          onChange={onRentCycleModeChange}
                        >
                          <MenuItem value="DAILY">Daily</MenuItem>
                          <MenuItem value="MONTHLY">Monthly</MenuItem>
                          <MenuItem value="CUSTOM">Custom</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    {rentCycleMode !== 'DAILY' && (
                      <Grid item xs={12} sm={4}>
                        <CustomNumberField
                          maxLength={3}
                          name="editCycleDays"
                          label="Cycle days"
                          value={
                            rentCycleMode === 'MONTHLY'
                              ? 30
                              : customCycleDays === 0
                                ? ''
                                : customCycleDays
                          }
                          onChange={onCustomCycleDaysChange}
                          fullWidth
                          disabled={rentCycleMode === 'MONTHLY'}
                          InputLabelProps={{ shrink: true }}
                        />
                      </Grid>
                    )}
                    {(rentCycleMode === 'MONTHLY' || rentCycleMode === 'CUSTOM') && (
                      <Grid container item xs={12} spacing={2}>
                        <Grid item xs={12} sm={8}>
                          <FormControl fullWidth size="small">
                            <InputLabel
                              id="site-edit-extra-day-charge-mode-label"
                              shrink
                            >
                              How to charge for extra days?
                            </InputLabel>
                            <Select
                              labelId="site-edit-extra-day-charge-mode-label"
                              label="How to charge for extra days?"
                              value={extraDayChargeMode}
                              onChange={onExtraDayChargeModeChange}
                            >
                              <MenuItem value="PER_DAY_RATE">Per Day Rate</MenuItem>
                              <MenuItem value="PER_DAY_FIXED">
                                Per Day Fixed
                              </MenuItem>
                              <MenuItem value="BY_DAY_RANGE">
                                By Day Range (different charges based on days)
                              </MenuItem>
                            </Select>
                          </FormControl>
                        </Grid>
                        {(extraDayChargeMode === 'PER_DAY_RATE' ||
                          extraDayChargeMode === 'PER_DAY_FIXED') && (
                          <Grid item xs={12} sm={4}>
                            <CustomNumberField
                              maxLength={12}
                              name="perDaySideField"
                              label={
                                extraDayChargeMode === 'PER_DAY_RATE'
                                  ? 'Per day Rate'
                                  : 'Per day fixed'
                              }
                              value={perDaySideField}
                              onChange={onPerDaySideFieldChange}
                              fullWidth
                              InputLabelProps={{ shrink: !!perDaySideField }}
                            />
                          </Grid>
                        )}
                      </Grid>
                    )}
                    {(rentCycleMode === 'MONTHLY' || rentCycleMode === 'CUSTOM') &&
                      extraDayChargeMode === 'BY_DAY_RANGE' &&
                      slabRows.map((row, idx) => {
                        const showAdd =
                          idx === slabRows.length - 1 && slabRows.length < 3
                        return (
                          <Grid
                            container
                            item
                            xs={12}
                            key={idx}
                            spacing={2}
                            sx={{ mt: -2.5 }}
                          >
                            <Grid item xs={12} sm={4}>
                              <CustomNumberField
                                maxLength={6}
                                name={`slab-upto-${idx}`}
                                label="Extra days"
                                value={row.uptoDays}
                                onChange={(e) =>
                                  updateSlab(idx, 'uptoDays', e.target.value)
                                }
                                fullWidth
                                error={slabError}
                              />
                            </Grid>
                            <Grid item xs={12} sm={4}>
                              <CustomNumberField
                                maxLength={8}
                                name={`slab-charge-${idx}`}
                                label="Charge"
                                value={row.charge}
                                onChange={(e) =>
                                  updateSlab(idx, 'charge', e.target.value)
                                }
                                fullWidth
                                error={slabError}
                              />
                            </Grid>
                            <Grid
                              item
                              xs={12}
                              sm={4}
                              sx={{
                                pt: { xs: 0, sm: '22px' },
                                display: 'flex',
                                alignItems: 'stretch',
                              }}
                            >
                              <Box
                                sx={{
                                  display: 'flex',
                                  gap: 1,
                                  width: '100%',
                                  alignItems: 'stretch',
                                  minHeight: 40,
                                }}
                              >
                                <Button
                                  type="button"
                                  variant="contained"
                                  disableElevation
                                  aria-label="Remove row"
                                  onClick={() => removeSlabRow(idx)}
                                  disabled={slabRows.length <= 1}
                                  sx={{
                                    flex: showAdd ? 1 : '1 1 auto',
                                    maxWidth: 40,
                                    minWidth: 0,
                                    minHeight: 40,
                                    height: 40,
                                    maxHeight: 40,
                                    alignSelf: 'flex-start',
                                    p: 0,
                                    bgcolor: '#c62828',
                                    color: '#fff',
                                    '&:hover': { bgcolor: '#b71c1c' },
                                    '&:disabled': {
                                      bgcolor: 'action.disabledBackground',
                                      color: 'action.disabled',
                                    },
                                  }}
                                >
                                  <RemoveIcon fontSize="small" />
                                </Button>
                                {showAdd ? (
                                  <Button
                                    type="button"
                                    variant="contained"
                                    disableElevation
                                    aria-label="Add row"
                                    onClick={addSlabRow}
                                    sx={{
                                      flex: 1,
                                      maxWidth: 40,
                                      minWidth: 40,
                                      minHeight: 40,
                                      height: 40,
                                      maxHeight: 40,
                                      alignSelf: 'flex-start',
                                      p: 0,
                                      bgcolor: '#2e7d32',
                                      color: '#fff',
                                      '&:hover': { bgcolor: '#1b5e20' },
                                    }}
                                  >
                                    <AddIcon fontSize="small" />
                                  </Button>
                                ) : null}
                              </Box>
                            </Grid>
                          </Grid>
                        )
                      })}
                  </Grid>
                </Grid>
              </Grid>
            </Box>
          </Grid>
        </Grid>
      </Box>
    </AppDialog>
  )
}

export default EditSiteItemRate
